import { Component, EventEmitter, Input, OnChanges, OnInit, Output } from '@angular/core';
import { AttachmentType, Complaint, ComplaintData, ComplaintPosition, DocumentAttachment } from '@icc/b2b/shared';
import { ComplaintService } from '../complaint.service';
import { Subscription } from 'rxjs';
import { NgxPermissionsService } from 'ngx-permissions';

@Component({
    selector: 'icc-complaint-position',
    templateUrl: './complaint-position.component.html',
    styleUrls: ['./complaint-position.component.scss'],
})
export class ComplaintPositionComponent implements OnChanges, OnInit{
    @Input() position!: ComplaintPosition;
    @Input() document!: Complaint;
    @Input() attachments: DocumentAttachment[] = [];
    @Input() showDetails = false;
    @Input() positionTitle: string[] = [];
    @Input() index = 0;
    @Input() preview = false;
    @Input() positionsCount = 0;
    @Input() selectedPositionsCount = 0;
    @Input() b2cForm = false;
    @Output() showDetailsEvent = new EventEmitter<boolean>();
    @Output() selectPosition = new EventEmitter<string>();
    @Output() unselectPosition = new EventEmitter<string>();

    editDefectMaintenance = false;

    hasAccessTo = {
        editPosition: false,
        movePositions: false,
    };

    selected = false;

    private subscriptions: Subscription[] = [];

    data: ComplaintData = {} as ComplaintData;
    defectMaintenance: number | null = null;

    constructor(
        private complaintService: ComplaintService,
        private permissionsService: NgxPermissionsService,
    ) {
        this.subscriptions.push(
            this.permissionsService.permissions$.subscribe(() => {
                this.checkAccess();
            }),
        );
    }

    ngOnInit() {
        this.loadData();
        this.defectMaintenance = this.position?.defectMaintenance ? this.position.defectMaintenance : null;
    }

    ngOnChanges() {
        this.checkAccess();
    }

    async loadData() {
        await this.complaintService.loaded;
        this.data = this.complaintService.data;
    }

    async checkAccess() {
        this.hasAccessTo.editPosition = await this.permissionsService.hasPermission('complaints-manage') && ((this.document.assignedTo && this.document.assignedTo.startsWith(this.complaintService.getUserType())) || await this.permissionsService.hasPermission('complaints-manage-manufacturer'));
        this.hasAccessTo.movePositions =
            this.hasAccessTo.editPosition &&
            this.complaintService.getUserType() === 'manufacturer' &&
            this.positionsCount > 1 &&
            (this.selected || this.positionsCount - 1 > this.selectedPositionsCount);
    }

    editPosition() {
        this.complaintService.editPosition(this.position, this.document, this.attachments);
    }

    switchShowDetails(showDetails: boolean) {
        this.showDetails = showDetails;
        this.showDetailsEvent.emit(showDetails);
    }

    getAttachment(
        attachmentType: AttachmentType
    ): DocumentAttachment | DocumentAttachment[] | undefined {
        if (attachmentType === 'additionalPhoto' || attachmentType === 'attachment') {
            return this.attachments.filter(
                (attachment) =>
                    attachment &&
                    attachment.attachmentType === attachmentType &&
                    attachment.documentPositionId === this.position.uuid
            );
        } else {
            return this.attachments.find(
                (attachment) =>
                    attachment &&
                    attachment.attachmentType === attachmentType &&
                    attachment.documentPositionId === this.position.uuid
            );
        }
    }

    getAdditionalAttachment(type: DocumentAttachment['attachmentType'], uuid?: string) {
        const attachment = this.attachments.find(
            (el) => el.attachmentType === type && (!uuid || el.uuid === uuid)
        );
        return attachment;
    }

    getAttachmentImg(uuid: string) {
        const attachment = this.attachments.find(
            (el) =>
                el &&
                el.uuid === uuid
        );
        if (attachment) {
            if (this.complaintService.cachedAttachments.has(attachment.uuid)) {
                return this.complaintService.cachedAttachments.get(attachment.uuid);
            }

            return '/complaint_attachments/' + attachment.uuid;
        }
        return '';
    }

    getAdditionalPhotosAndAttachments() {
        return this.attachments.filter(
            (el) => el.attachmentType === 'additionalPhoto' || el.attachmentType === 'attachment'
        );
    }

    getIconName(filename: string): string {
        const extension = filename.split('.').pop()?.toLowerCase();
        switch (extension) {
            case 'pdf': return 'pdf_file';
            case 'jpg':
            case 'jpeg':
            case 'png': return 'image_file';
            default: return 'file';
        }
    }

    getIconColor(filename: string): string {
        const extension = filename.split('.').pop()?.toLowerCase();
        switch (extension) {
            case 'pdf': return '#E74856';
            case 'jpg':
            case 'jpeg':
            case 'png': return '#008231';
            default: return '#C19C00';
        }
    }

    changeDefectMaintenance(defectMaintenance: number) {
        if (this.position.defectMaintenance !== defectMaintenance) {
            this.complaintService.changeDefectMaintenance(
                this.document,
                this.position,
                defectMaintenance
            );
        } else {
            return;
        }
    }

    selectPositionEvent() {
        this.selected = !this.selected;
        if (this.selected) {
            this.selectPosition.emit(this.position.uuid);
        } else {
            this.unselectPosition.emit(this.position.uuid);
        }
    }

    getMaintenanceInstructionById(id: number): string {
        if (!this.data || !this.data.defectMaintenances) {
            return '';
        }
        return this.data.defectMaintenances.find(dm => dm.id === id)?.maintenanceInstruction || '';
    }

    getTranslatedProductType() {
        return this.complaintService.getTranslatedProductType(this.position.complaintProductTypeId);
    }
}
